import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from "react";

interface PendingNavigation {
  path: string;
  type: "link" | "popstate";
}

interface NavigationBlockerContextType {
  isBlocked: boolean;
  pendingNavigation: PendingNavigation | null;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  attemptNavigation: (path: string) => boolean;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
  setPendingNavigation: (nav: PendingNavigation | null) => void;
  setIsBlocked: (value: boolean) => void;
}

const NavigationBlockerContext = createContext<NavigationBlockerContextType | null>(null);

export const NavigationBlockerProvider = ({ children }: { children: ReactNode }) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);

  const attemptNavigation = useCallback(
    (path: string): boolean => {
      if (hasUnsavedChanges) {
        setPendingNavigation({ path, type: "link" });
        setIsBlocked(true);
        return false;
      }
      return true;
    },
    [hasUnsavedChanges]
  );

  const confirmNavigation = useCallback(() => {
    setIsBlocked(false);
    setHasUnsavedChanges(false);
  }, []);

  const cancelNavigation = useCallback(() => {
    setIsBlocked(false);
    setPendingNavigation(null);
  }, []);

  const value = useMemo(
    () => ({
      isBlocked,
      pendingNavigation,
      hasUnsavedChanges,
      setHasUnsavedChanges,
      attemptNavigation,
      confirmNavigation,
      cancelNavigation,
      setPendingNavigation,
      setIsBlocked,
    }),
    [
      isBlocked,
      pendingNavigation,
      hasUnsavedChanges,
      attemptNavigation,
      confirmNavigation,
      cancelNavigation,
    ]
  );

  return (
    <NavigationBlockerContext.Provider value={value}>
      {children}
    </NavigationBlockerContext.Provider>
  );
};

export const useNavigationBlocker = () => {
  const context = useContext(NavigationBlockerContext);
  if (!context) {
    throw new Error("useNavigationBlocker must be used within a NavigationBlockerProvider");
  }
  return context;
};
